/* SectionsConfigPanel — Phase B admin.
 *
 * Left rail: sections grouped by page.
 * Right: config panel for the selected section with drag-reorder list,
 * cover selector, slideshow config, and a "Pick from Library" modal.
 *
 * Everything auto-saves — no publish button, per user's ask.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Search, Image as ImageIcon, X, ChevronRight,
  Star, StarOff, Trash2, Upload, GripVertical, Plus, Play,
  Pause, Sliders, Sparkles, Save, RefreshCw, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/site-sections`;
const MEDIA_API = `${BACKEND_URL}/api/admin/media`;
const SERVE = `${BACKEND_URL}/api/media/serve`;

// Sections currently mounted on the public site via <SectionMedia>.
// Anything NOT in this list is still configurable in the admin panel
// but won't actually display on any page until a dev adds a mount.
// Update this list whenever you add a new <SectionMedia> to a page.
const MOUNTED_SECTIONS = new Set([
  'home-mission', 'home-values', 'home-location',
  'about-hero', 'about-team',
  'events-gallery',
  'country-haiti-hero', 'country-dominican-republic-hero',
  'country-colombia-yumbo-hero', 'country-elsalvador-hero',
  'country-ecuador-hero', 'country-jamaica-hero',
]);

const PAGE_LABELS = {
  home: 'Home', about: 'About', donate: 'Donate', events: 'Events',
  involved: 'Get Involved', contact: 'Contact',
  haiti: 'Haiti', 'new-york': 'New York', colombia: 'Colombia',
  jamaica: 'Jamaica', ecuador: 'Ecuador',
};

const SectionsConfigPanel = () => {
  const [sections, setSections] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, { withCredentials: true });
      setSections(r.data.rows || []);
      if (!selectedKey && r.data.rows?.length) {
        setSelectedKey(r.data.rows[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load sections.');
    }
    setLoading(false);
  }, [selectedKey]);

  const loadDetail = useCallback(async (key) => {
    if (!key) { setDetail(null); return; }
    try {
      const r = await axios.get(`${API}/${key}`, { withCredentials: true });
      setDetail(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load section.');
    }
  }, []);

  useEffect(() => { loadSections(); }, [loadSections]);
  useEffect(() => { loadDetail(selectedKey); }, [selectedKey, loadDetail]);

  const grouped = sections.reduce((acc, s) => {
    (acc[s.page] = acc[s.page] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="sections-panel">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-heading font-bold text-base text-[#0A0A0A]
                          inline-flex items-center gap-2">
              <Sliders className="w-4 h-4 text-[#BA1826]" />
              Section Media Configuration
            </h2>
            <p className="text-[11px] text-[#4B5563] mt-1">
              Each region of the public site independently supports a
              single image or a slideshow. Changes auto-save.
            </p>
          </div>
          <button onClick={loadSections}
            className="p-1.5 text-[#4B5563] hover:text-[#BA1826]"
            data-testid="sections-refresh"
            title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700
                       text-[11px] p-2 rounded inline-flex items-start gap-2"
          data-testid="sections-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError('')}><X className="w-3 h-3" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
        {/* Left rail */}
        <div className="bg-white border border-[#E5E7EB] rounded p-2 max-h-[600px]
                       overflow-y-auto" data-testid="sections-rail">
          {Object.entries(grouped).map(([page, rows]) => (
            <div key={page} className="mb-3">
              <p className="text-[9px] uppercase tracking-wider font-bold
                          text-[#9CA3AF] px-2 mb-1">
                {PAGE_LABELS[page] || page}
              </p>
              <ul className="space-y-0.5">
                {rows.map((s) => (
                  <li key={s.id}>
                    <button onClick={() => setSelectedKey(s.id)}
                      className={`w-full text-left text-[11px] px-2 py-1.5 rounded
                        flex items-center gap-1.5
                        ${selectedKey === s.id
                          ? 'bg-[#BA1826] text-white'
                          : 'hover:bg-[#FAFBFC] text-[#0A0A0A]'}`}
                      data-testid={`section-${s.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0
                        ${MOUNTED_SECTIONS.has(s.id)
                          ? (selectedKey === s.id ? 'bg-white' : 'bg-emerald-500')
                          : (selectedKey === s.id ? 'bg-white/40' : 'bg-[#D1D5DB]')}`}
                        title={MOUNTED_SECTIONS.has(s.id)
                          ? 'Live on the public site'
                          : 'Configured here, not yet mounted on any page'} />
                      <span className="flex-1 truncate">
                        {s.label.replace(/^[^—]+—\s*/, '')}
                      </span>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold
                        ${selectedKey === s.id
                          ? 'bg-white/20 text-white'
                          : s.display_mode === 'slideshow'
                            ? 'bg-sky-100 text-sky-700'
                            : 'bg-[#F1F5F9] text-[#4B5563]'}`}>
                        {(s.media_ids || []).length || 0}
                        {s.display_mode === 'slideshow' ? ' 📽' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Right: detail editor */}
        <div className="bg-white border border-[#E5E7EB] rounded p-4"
          data-testid="section-editor">
          {!detail ? (
            <div className="text-center py-16 text-xs text-[#9CA3AF]">
              Pick a section from the left to configure it.
            </div>
          ) : (
            <SectionEditor detail={detail}
              onChanged={() => { loadSections(); loadDetail(selectedKey); }}
              onError={setError} />
          )}
        </div>
      </div>
    </div>
  );
};

const SectionEditor = ({ detail, onChanged, onError }) => {
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [albumPreview, setAlbumPreview] = useState([]);

  // Load albums once so the "From Album" source picker can list them.
  useEffect(() => {
    axios.get(`${MEDIA_API}/albums`, { withCredentials: true })
      .then((r) => setAlbums(r.data.rows || []))
      .catch(() => { /* silent */ });
  }, []);

  // When section is in album mode, fetch the current public materialized
  // list so the admin sees exactly what public visitors will see.
  useEffect(() => {
    if (detail.source_type !== 'album' || !detail.source_album_id) {
      setAlbumPreview([]);
      return;
    }
    const alb = albums.find((a) => a.id === detail.source_album_id);
    if (!alb) return;
    axios.get(`${BACKEND_URL}/api/public/media/albums/${alb.slug}/photos`,
                { params: { limit: 12 } })
      .then((r) => setAlbumPreview(r.data.photos || []))
      .catch(() => setAlbumPreview([]));
  }, [detail.source_type, detail.source_album_id, albums]);

  const patch = async (payload) => {
    setSaving(true); onError('');
    try {
      await axios.patch(`${API}/${detail.id}`, payload,
        { withCredentials: true });
      onChanged();
    } catch (e) {
      onError(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  const addMedia = async (mediaId) => {
    onError('');
    try {
      await axios.post(`${API}/${detail.id}/media/${mediaId}`, {},
        { withCredentials: true });
      onChanged();
    } catch (e) {
      onError(e.response?.data?.detail || 'Failed to add media.');
    }
  };

  const removeMedia = async (mediaId) => {
    onError('');
    try {
      await axios.delete(`${API}/${detail.id}/media/${mediaId}`,
        { withCredentials: true });
      onChanged();
    } catch (e) {
      onError(e.response?.data?.detail || 'Failed to remove.');
    }
  };

  const reorder = async (newIds) => {
    onError('');
    try {
      await axios.post(`${API}/${detail.id}/reorder`, { media_ids: newIds },
        { withCredentials: true });
      onChanged();
    } catch (e) {
      onError(e.response?.data?.detail || 'Reorder failed.');
    }
  };

  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const ids = [...(detail.media_ids || [])];
    const [moved] = ids.splice(dragIdx, 1);
    ids.splice(targetIdx, 0, moved);
    setDragIdx(null); setDragOverIdx(null);
    reorder(ids);
  };

  const setCover = (mediaId) => patch({ cover_media_id: mediaId });

  const handleDirectUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true); onError('');
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('files', f));
      await axios.post(`${API}/${detail.id}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' } });
      onChanged();
    } catch (e) {
      onError(e.response?.data?.detail || 'Upload failed.');
    }
    setUploading(false);
  };

  const isSlideshow = detail.display_mode === 'slideshow';
  // Universal Media Standard — Phase B: max 6 media per slideshow.
  // Legacy sections may already hold more; we render them untouched
  // but block adding more (backend also enforces with HTTP 422).
  const SLIDESHOW_MAX = 6;
  const manualMediaCount = (detail.media_ids || []).length;
  const atManualCap = isSlideshow
    && detail.source_type !== 'album'
    && manualMediaCount >= SLIDESHOW_MAX;
  const overManualCap = isSlideshow
    && detail.source_type !== 'album'
    && manualMediaCount > SLIDESHOW_MAX;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 pb-3
                     border-b border-[#F1F5F9]">
        <div>
          <h3 className="text-sm font-bold text-[#0A0A0A]">{detail.label}</h3>
          <p className="text-[10px] text-[#4B5563]">
            <code>{detail.id}</code> · Last updated{' '}
            {detail.updated_at ? new Date(detail.updated_at).toLocaleDateString() : '—'}{' '}
            by {(detail.updated_by || '').split('@')[0] || '—'}
          </p>
        </div>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#BA1826]" />}
      </div>

      {!MOUNTED_SECTIONS.has(detail.id) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900
                       text-[11px] p-2 rounded inline-flex items-start gap-1.5"
          data-testid="not-mounted-notice">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <strong>This section isn&apos;t rendered on the public site yet.</strong>{' '}
            You can configure it here, but the images won&apos;t appear anywhere
            until a developer mounts <code>&lt;SectionMedia sectionKey=&quot;{detail.id}&quot; /&gt;</code>
            {' '}on the relevant page.
          </div>
        </div>
      )}

      {/* Display mode toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {['single', 'slideshow'].map((m) => (
          <button key={m} onClick={() => patch({ display_mode: m })}
            className={`text-xs px-3 py-1.5 rounded border font-medium ${
              detail.display_mode === m
                ? 'bg-[#BA1826] text-white border-[#BA1826]'
                : 'bg-white text-[#4B5563] border-[#E5E7EB]'
            }`}
            data-testid={`mode-${m}`}>
            {m === 'single' ? 'Single Image' : 'Slideshow'}
          </button>
        ))}
      </div>

      {/* Source toggle — Manual (curated list) vs From Album (dynamic) */}
      <div className="border border-[#E5E7EB] rounded p-3 bg-[#FAFBFC]"
        data-testid="source-controls">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-2">
          Where do the images come from?
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => patch({ source_type: 'manual' })}
            className={`text-xs px-3 py-1.5 rounded border font-medium ${
              detail.source_type !== 'album'
                ? 'bg-[#BA1826] text-white border-[#BA1826]'
                : 'bg-white text-[#4B5563] border-[#E5E7EB]'
            }`}
            data-testid="source-manual">
            Manual (curated list)
          </button>
          <button
            onClick={() => {
              // If the currently-stored source_album_id references an
              // album that no longer exists (deleted since it was set),
              // fall through to the first available album so we don't
              // 400 on the PATCH. Same defensive treatment as unset.
              const known = albums.find(
                (a) => a.id === detail.source_album_id);
              if (!known) {
                if (!albums.length) return;
                patch({ source_type: 'album',
                          source_album_id: albums[0].id });
              } else {
                patch({ source_type: 'album' });
              }
            }}
            disabled={!albums.length}
            className={`text-xs px-3 py-1.5 rounded border font-medium
              disabled:opacity-50 ${
              detail.source_type === 'album'
                ? 'bg-[#BA1826] text-white border-[#BA1826]'
                : 'bg-white text-[#4B5563] border-[#E5E7EB]'
            }`}
            data-testid="source-album">
            <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />
            From Album (dynamic)
          </button>
        </div>

        {detail.source_type === 'album' && (
          <div className="mt-3 space-y-2">
            <div>
              <p className="text-[10px] text-[#4B5563] mb-1">
                Pick an album — its <strong>active</strong> photos will
                populate this section, newest first.
              </p>
              <select
                value={detail.source_album_id || ''}
                onChange={(e) => patch({ source_album_id: e.target.value })}
                className="w-full text-[11px] px-2 py-1.5 border
                          border-[#E5E7EB] rounded bg-white"
                data-testid="source-album-picker">
                <option value="">— pick an album —</option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.media_count} photo{a.media_count === 1 ? '' : 's'})
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded p-2
                           text-[11px] text-sky-900"
              data-testid="album-info">
              <Sparkles className="w-3 h-3 inline mr-0.5" />
              <strong>Auto-updating:</strong> Any new photo uploaded to
              this album will appear here automatically, no editing this
              page again.
              {isSlideshow && (
                <span className="block mt-1 text-[10px] text-sky-800">
                  Public slideshow shows the newest {SLIDESHOW_MAX} photos from this album.
                  The album itself keeps growing unlimited.
                </span>
              )}
            </div>
            {albumPreview.length > 0 && (
              <div data-testid="album-preview-grid">
                <p className="text-[10px] text-[#4B5563] mb-1">
                  Current album contents ({albumPreview.length}
                  {albumPreview.length === 12 ? '+' : ''} photos, newest first)
                </p>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-1">
                  {albumPreview.slice(0, 12).map((p) => (
                    <img key={p.id}
                      src={`${SERVE}/${p.id}/thumb`}
                      alt={p.alt_text || p.title}
                      className="aspect-square w-full object-cover rounded
                                border border-[#E5E7EB]" />
                  ))}
                </div>
              </div>
            )}
            {detail.source_album_id && albumPreview.length === 0 && (
              <p className="text-[11px] text-amber-700 italic">
                This album has no active photos yet — upload some in the
                Media Library and they&apos;ll appear here automatically.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Media list w/ drag-reorder — MANUAL SOURCE ONLY.
          In album mode this content is dynamically pulled + shown as
          a preview above; there's nothing to hand-order here. */}
      {detail.source_type !== 'album' && (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">
            Images ({manualMediaCount}{isSlideshow ? `/${SLIDESHOW_MAX}` : ''})
            {isSlideshow && (
              <span className="ml-1 text-[9px] font-normal normal-case text-[#9CA3AF]">
                · max {SLIDESHOW_MAX} per slideshow
              </span>
            )}
          </p>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setPickerOpen(true)}
              disabled={atManualCap || overManualCap}
              className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded
                        inline-flex items-center gap-0.5 hover:bg-[#FAFBFC]
                        disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pick-from-library">
              <Plus className="w-2.5 h-2.5" /> Pick from Library
            </button>
            <label className={`text-[10px] px-2 py-1 bg-[#BA1826] text-white
                             rounded inline-flex items-center gap-0.5
                             ${atManualCap || overManualCap
                               ? 'opacity-40 cursor-not-allowed'
                               : 'cursor-pointer'}`}>
              {uploading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Upload className="w-2.5 h-2.5" />}
              Upload
              <input type="file" multiple accept="image/*" className="hidden"
                disabled={atManualCap || overManualCap}
                onChange={(e) => { handleDirectUpload(e.target.files); e.target.value = ''; }}
                data-testid="section-upload-input" />
            </label>
          </div>
        </div>
        {overManualCap && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900
                         text-[11px] p-2 rounded inline-flex items-start gap-1.5 mb-1.5"
            data-testid="over-cap-notice">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <strong>Legacy slideshow with {manualMediaCount} photos.</strong>{' '}
              Public renders unchanged, but the new standard caps at {SLIDESHOW_MAX}. Remove
              photos to bring this in line — you can&apos;t add more until you do.
            </div>
          </div>
        )}
        {(detail.media || []).length === 0 ? (
          <div className="text-center py-6 text-[11px] text-[#9CA3AF]
                         border border-dashed border-[#E5E7EB] rounded italic">
            No images yet. Pick from your Library or upload directly.
          </div>
        ) : (
          <ul className="space-y-1" data-testid="section-media-list">
            {(detail.media || []).map((m, idx) => (
              <li key={m.id}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                onDrop={() => handleDrop(idx)}
                className={`flex items-center gap-2 p-1.5 rounded border
                  ${dragOverIdx === idx && dragIdx !== idx
                    ? 'bg-sky-50 border-sky-300'
                    : 'bg-white border-[#E5E7EB]'}
                  ${dragIdx === idx ? 'opacity-40' : ''}`}
                data-testid={`section-media-row-${idx}`}>
                <GripVertical className="w-3 h-3 text-[#9CA3AF] cursor-move" />
                <img src={`${SERVE}/${m.id}/thumb`} alt={m.alt_text || m.title}
                  className="w-10 h-10 object-cover rounded border border-[#E5E7EB]" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">
                    {m.title || m.filename}
                  </p>
                  <p className="text-[10px] text-[#4B5563]">
                    {m.width}×{m.height}
                  </p>
                </div>
                <button onClick={() => setCover(m.id)}
                  className={`p-1 ${
                    detail.cover_media_id === m.id
                      ? 'text-amber-500' : 'text-[#9CA3AF] hover:text-amber-500'
                  }`}
                  title={detail.cover_media_id === m.id
                    ? 'Cover image (shown first)' : 'Make cover'}
                  data-testid={`cover-toggle-${idx}`}>
                  {detail.cover_media_id === m.id
                    ? <Star className="w-3.5 h-3.5 fill-current" />
                    : <StarOff className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => removeMedia(m.id)}
                  className="p-1 text-[#9CA3AF] hover:text-rose-600"
                  data-testid={`remove-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {/* Slideshow-only controls */}
      {isSlideshow && (
        <div className="border border-[#E5E7EB] rounded p-3 space-y-3
                       bg-[#FAFBFC]" data-testid="slideshow-controls">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">
            Slideshow settings
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-[#4B5563] mb-1">Transition</p>
              <div className="flex gap-1">
                {['fade', 'slide'].map((t) => (
                  <button key={t} onClick={() => patch({ transition: t })}
                    className={`text-[10px] px-2 py-1 rounded border flex-1 ${
                      detail.transition === t
                        ? 'bg-[#BA1826] text-white border-[#BA1826]'
                        : 'bg-white text-[#4B5563] border-[#E5E7EB]'
                    }`}
                    data-testid={`tx-${t}`}>
                    {t === 'fade' ? 'Fade' : 'Slide'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] text-[#4B5563] mb-1">
                Speed: {(detail.autoplay_interval_ms / 1000).toFixed(1)}s
              </p>
              <input type="range" min={2000} max={15000} step={500}
                value={detail.autoplay_interval_ms}
                onChange={(e) => patch({ autoplay_interval_ms: parseInt(e.target.value, 10) })}
                className="w-full accent-[#BA1826]"
                data-testid="speed-slider" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
            {[
              ['autoplay', 'Autoplay'],
              ['loop', 'Loop'],
              ['show_arrows', 'Nav arrows'],
              ['show_dots', 'Pagination dots'],
            ].map(([key, label]) => (
              <label key={key} className="inline-flex items-center gap-1.5
                                          cursor-pointer">
                <input type="checkbox" checked={!!detail[key]}
                  onChange={(e) => patch({ [key]: e.target.checked })}
                  className="accent-[#BA1826]"
                  data-testid={`toggle-${key}`} />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Preview — shows the first image users will see. In album
          mode, this is the newest album photo (matches public order). */}
      {(() => {
        const previewImgId = detail.source_type === 'album'
          ? albumPreview[0]?.id
          : (detail.cover_media_id || detail.media?.[0]?.id);
        const count = detail.source_type === 'album'
          ? albumPreview.length
          : (detail.media || []).length;
        if (!previewImgId) return null;
        return (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold
                         text-[#4B5563] mb-1.5">Preview</p>
            <div className="rounded overflow-hidden border border-[#E5E7EB]
                           bg-black aspect-video max-h-72">
              <img src={`${SERVE}/${previewImgId}/desktop`}
                alt="preview"
                className="w-full h-full object-cover"
                data-testid="section-preview" />
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-1 italic">
              {detail.source_type === 'album'
                ? `Dynamic slideshow · ${count}${count === 12 ? '+' : ''} photos from album · ${detail.transition} · ${(detail.autoplay_interval_ms/1000).toFixed(1)}s`
                : isSlideshow
                  ? `Slideshow: ${count} images, ${detail.transition} · ${(detail.autoplay_interval_ms/1000).toFixed(1)}s`
                  : 'Single image (starred image shown above)'}
            </p>
          </div>
        );
      })()}

      {pickerOpen && (
        <MediaPickerModal
          selectedIds={detail.media_ids || []}
          onClose={() => setPickerOpen(false)}
          onPick={async (mid) => { await addMedia(mid); }}
        />
      )}
    </div>
  );
};

const MediaPickerModal = ({ selectedIds, onClose, onPick }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(MEDIA_API, {
        withCredentials: true,
        params: { limit: 100, status: 'active', q: q.trim() || undefined },
      });
      setItems(r.data.rows || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center
                   justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-4 w-full max-w-4xl max-h-[80vh]
                     flex flex-col" onClick={(e) => e.stopPropagation()}
        data-testid="media-picker">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Pick from Media Library</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2
                            -translate-y-1/2 text-[#9CA3AF]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search library…"
            className="w-full pl-8 pr-3 py-1.5 text-[11px] border
                      border-[#E5E7EB] rounded"
            data-testid="picker-search" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-xs text-[#9CA3AF]">
              No media matches. Upload some in the Media Library tab first.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {items.map((m) => {
                const already = selectedIds.includes(m.id);
                return (
                  <button key={m.id}
                    onClick={() => !already && onPick(m.id)}
                    disabled={already}
                    className={`relative aspect-square rounded overflow-hidden
                      border ${already
                        ? 'border-emerald-500 opacity-60'
                        : 'border-[#E5E7EB] hover:border-[#BA1826]'}`}
                    data-testid={`picker-${m.id}`}>
                    <img src={`${SERVE}/${m.id}/thumb`}
                      alt={m.alt_text || m.title}
                      className="w-full h-full object-cover" />
                    {already && (
                      <span className="absolute inset-0 flex items-center
                                      justify-center bg-emerald-500/40
                                      text-white text-[9px] font-bold">
                        Already added
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionsConfigPanel;
